/**
 * The organization's first day of the week (#864) — one definition of "which
 * week" for the dashboard's Week period, the weekly series on the server and
 * the weekly digest.
 *
 * Stored as `orgs.settings_json.weekStartsOn`, an ISO weekday: 1 = Monday …
 * 7 = Sunday. Absent or unreadable means Monday, so an organization that never
 * chose keeps the weeks it always had.
 *
 * Pure date math, no Node API: the browser computes its period with it, the
 * workers bucket and cut digest windows with it, and all three must agree on
 * where a week begins. The clocks differ on purpose and say so in the name:
 * `startOfWeek` reads the runtime's local clock (the browser's), while
 * `localDayKey` + `weekStartDay` and `previousCalendarWeek` work in an
 * explicit IANA zone (the org's — the server's weekly series and the digest).
 */

import { zoneDayOf, zoneDays, zoneSegments, type ZoneSegment } from './zone-days';

/** ISO weekday the week starts on: 1 = Monday … 7 = Sunday. */
export type WeekStartsOn = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Every value, Monday first — the order a settings control lists them in. */
export const WEEK_STARTS_ON_VALUES: readonly WeekStartsOn[] = [1, 2, 3, 4, 5, 6, 7];

/** Monday — what an organization without the setting has always had. */
export const DEFAULT_WEEK_STARTS_ON: WeekStartsOn = 1;

const DAY_MS = 24 * 60 * 60 * 1000;

export function isWeekStartsOn(value: unknown): value is WeekStartsOn {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 7;
}

/**
 * The org's first day of the week from its settings — the parsed object or
 * the raw `settings_json` string. Anything that is not 1..7 reads as Monday:
 * a week boundary is a display preference, and a broken value must not take a
 * dashboard or a digest down with it.
 */
export function readWeekStartsOn(settings: unknown): WeekStartsOn {
  let parsed: unknown = settings;
  if (typeof settings === 'string') {
    try {
      parsed = JSON.parse(settings);
    } catch {
      return DEFAULT_WEEK_STARTS_ON;
    }
  }
  if (!parsed || typeof parsed !== 'object') return DEFAULT_WEEK_STARTS_ON;
  const value = (parsed as { weekStartsOn?: unknown }).weekStartsOn;
  return isWeekStartsOn(value) ? value : DEFAULT_WEEK_STARTS_ON;
}

/** ISO weekday (1 = Mon … 7 = Sun) of a `getDay()` / `getUTCDay()` value (0 = Sun). */
export function isoWeekday(jsDay: number): number {
  return jsDay === 0 ? 7 : jsDay;
}

/**
 * How many days `isoDay` lies after the first day of its week: 0 on the first
 * day itself, 6 on the day before it.
 *
 * Not `getDate() - getDay() + 1`: `getDay()` is 0 on Sunday, so that formula
 * lands on TOMORROW — every Sunday the "current week" pointed at the future
 * week and every dashboard rendered empty (found live 2026-07-19).
 */
export function daysIntoWeek(isoDay: number, weekStartsOn: WeekStartsOn): number {
  return (((isoDay - weekStartsOn) % 7) + 7) % 7;
}

/**
 * Local midnight (the RUNTIME's clock — in the browser, the user's) of the
 * first day of the week containing `date`. The dashboard's Week period, its
 * navigation, its label and "is this the current week" all start here.
 */
export function startOfWeek(date: Date, weekStartsOn: WeekStartsOn): Date {
  const start = new Date(date);
  start.setDate(date.getDate() - daysIntoWeek(isoWeekday(date.getDay()), weekStartsOn));
  start.setHours(0, 0, 0, 0);
  return start;
}

/** `YYYY-MM-DD` → its UTC midnight. */
function dayKeyMs(dayKey: string): number {
  return Date.UTC(
    Number(dayKey.slice(0, 4)),
    Number(dayKey.slice(5, 7)) - 1,
    Number(dayKey.slice(8, 10))
  );
}

function dayKeyOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** The calendar day `days` after `dayKey` (negative — before). */
export function addDays(dayKey: string, days: number): string {
  return dayKeyOf(dayKeyMs(dayKey) + days * DAY_MS);
}

/** The first day (`YYYY-MM-DD`) of the week containing the calendar day `dayKey`. */
export function weekStartDay(dayKey: string, weekStartsOn: WeekStartsOn): string {
  const ms = dayKeyMs(dayKey);
  const back = daysIntoWeek(isoWeekday(new Date(ms).getUTCDay()), weekStartsOn);
  return dayKeyOf(ms - back * DAY_MS);
}

/** The local calendar day (`YYYY-MM-DD`) of `instant` in `timeZone`. Throws on an unknown zone. */
export function localDayKey(instant: Date, timeZone: string): string {
  const t = instant.getTime();
  return zoneDayOf(zoneSegments(timeZone, t, t + 1), t);
}

/**
 * The first and the last instant of the local calendar day `localDay` in
 * `timeZone`, the way every other count cuts days (`zoneDays`, #1235): the
 * first instant whose local date IS that day. A day that begins with the
 * clocks jumping at midnight (Santiago, 6 Sep 2026: 00:00 -> 01:00) starts at
 * the jump, not an hour before it in the day before. The range sampled is a
 * few days wide: every zone's midnight of `localDay` lies inside it.
 *
 * `localDay` is the date at UTC midnight (`Date.UTC(y, m - 1, d)`; overflowing
 * months and days normalize). A day the zone skipped outright (Samoa, 30 Dec
 * 2011) has no instant: an empty window at the next day's start, which counts
 * nothing, not a throw. Throws on an unknown zone, like `Intl`. The weekly
 * digest's week, the daily digest's objections day and the monthly Voice of
 * Customer window all cut their days here.
 */
export function localDayBounds(
  localDay: Date,
  timeZone: string
): { start: Date; end: Date } {
  const centre = localDay.getTime();
  // Every zone's midnight of the day lies within this range (UTC−12 … UTC+14).
  const from = centre - 2 * DAY_MS;
  const to = centre + 3 * DAY_MS;
  const key = localDay.toISOString().slice(0, 10);
  const days = zoneDays(zoneSegments(timeZone, from, to), from, to);
  const found = days.find((d) => d.day === key);
  if (found) return { start: new Date(found.start), end: new Date(found.end - 1) };
  const at = new Date(days.find((d) => d.day > key)?.start ?? centre);
  return { start: at, end: new Date(at.getTime() - 1) };
}

/**
 * The first instant of the local calendar day `dayKey` (`YYYY-MM-DD`) in
 * `timeZone` — `localDayBounds(...).start`. A day the zone skipped outright
 * starts where the next one does. Throws on an unknown zone.
 */
export function localDayStart(dayKey: string, timeZone: string): Date {
  return localDayBounds(new Date(dayKeyMs(dayKey)), timeZone).start;
}

/** A half-open window `[start, end)`. */
export interface WeekWindow {
  start: Date;
  end: Date;
}

/**
 * The calendar week that ended when the week containing `dayKey` began, and
 * the week before it — both half-open, both cut in `timeZone` at local
 * midnight of `weekStartsOn`. The weekly digest reports the first and trends
 * against the second (#864).
 *
 * Cut in calendar days, not in 7 × 24 h: a week with a clock change is 167 or
 * 169 hours long, and its boundaries still sit at local midnight — the
 * boundaries the dashboard draws for the same week.
 */
export function previousCalendarWeek(
  dayKey: string,
  timeZone: string,
  weekStartsOn: WeekStartsOn
): WeekWindow & { previous: WeekWindow } {
  const thisWeek = weekStartDay(dayKey, weekStartsOn);
  const end = localDayStart(thisWeek, timeZone);
  const start = localDayStart(addDays(thisWeek, -7), timeZone);
  const previousStart = localDayStart(addDays(thisWeek, -14), timeZone);
  return { start, end, previous: { start: previousStart, end: start } };
}

export type ZoneBucketUnit = 'day' | 'week' | 'month';

export interface ZoneBucket {
  /** The bucket's first local day `YYYY-MM-DD` (a week — its first day, a month — `YYYY-MM-01` or the window's first day). */
  label: string;
  /** Epoch ms, half-open `[start, end)`, clipped to the window. */
  start: number;
  end: number;
}

/**
 * Local days, weeks or months of `[start, end)` (#2394 §9 п. 5): the days of
 * {@link zoneDays} — one definition of «which day» — folded into the week
 * that starts on `weekStartsOn` or into the calendar month. The first and the
 * last bucket are clipped to the window, as the days are.
 */
export function zoneBuckets(
  segments: readonly ZoneSegment[],
  start: number,
  end: number,
  unit: ZoneBucketUnit,
  weekStartsOn: WeekStartsOn = DEFAULT_WEEK_STARTS_ON
): ZoneBucket[] {
  const keyOf = (day: string): string =>
    unit === 'day'
      ? day
      : unit === 'week'
        ? weekStartDay(day, weekStartsOn)
        : day.slice(0, 7);
  const buckets: (ZoneBucket & { key: string })[] = [];
  for (const day of zoneDays(segments, start, end)) {
    const key = keyOf(day.day);
    const last = buckets[buckets.length - 1];
    if (last && last.key === key) last.end = day.end;
    else buckets.push({ key, label: day.day, start: day.start, end: day.end });
  }
  return buckets.map(({ label, start: from, end: to }) => ({
    label,
    start: from,
    end: to,
  }));
}
